import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, map } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { HttpBaseService } from 'src/app/base/http-base.service';
import { AppGlobalService } from 'src/app/services/app.global.service';
import {
  RefType,
  BranchFilters,
  TagFilters,
  LastUpdatedDetails,
  FilterOption,
  BranchTreeNode,
  TagTreeNode,
  MergeChangesRequest,
  AutoSelectRequest,
  MergeBranchRequest
} from './repo-management.types';

// API response structure (what the backend returns)
interface ApiBranchOrTagResponse {
  results: Record<string, unknown>[];
  page: number;
  pageSize: number;
  total: number;
  filtered: number;
}

// Internal response structure (what the component expects)
interface BranchOrTagResponse {
  data: (BranchTreeNode | TagTreeNode)[];
  total: number;
}

interface FilterOptionsResponse {
  data: string[];
  /** For sourceBranchAppSid, maps SID to branch name */
  labelMap?: Record<string, string>;
}

export interface GetBranchesOrTagsParams {
  orgId: string;
  refType: RefType;
  filters: BranchFilters | TagFilters;
  sort?: string;
  dir?: string;
  page: number;
  length: number;
  parentAppSid: string;
  suiteMode?: boolean;
}

@Injectable({ providedIn: 'root' })
export class RepoManagementService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly httpBaseService = inject(HttpBaseService);
  private readonly appGlobalService = inject(AppGlobalService);

  private getHeaders(appIdOverride?: string): HttpHeaders {
    const appId = appIdOverride || this.appGlobalService.get('appId') as string;
    return new HttpHeaders({
      'x-rappit-application': appId,
      'x-rappit-organization': this.appGlobalService.get('orgId') as string
    });
  }

  getBranchesOrTags(params: GetBranchesOrTagsParams): Observable<BranchOrTagResponse> {
    const { orgId, refType, filters, sort, dir, page, length, parentAppSid, suiteMode } = params;

    // Handle global search separately from other filters
    const { _global, ...otherFilters } = filters as (BranchFilters | TagFilters) & { _global?: string };
    const payload: Record<string, unknown> = {
      search: {
        ...otherFilters,
        refType: refType,
        orgId: orgId,
        ..._global && typeof _global === 'string' && _global.trim() ? { _global: _global.trim() } : {}
      },
      start: (page - 1) * length,
      length: length
    };

    // Only add order if dir is provided, and always as an array
    if (dir) {
      payload['order'] = [
        {
          column: sort,
          dir
        }
      ];
    }

    // Helper to check if value is empty
    const isEmpty = (val: unknown): boolean =>
      val === null || val === undefined || val === '' || (Array.isArray(val) && val.length === 0);

    // Clean payload recursively
    const cleanObject = (obj: Record<string, unknown>): void => {
      Object.keys(obj).forEach((key) => {
        if (isEmpty(obj[key])) {
          delete obj[key];
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key] !== null) {
          cleanObject(obj[key] as Record<string, unknown>);
          if (Object.keys(obj[key] as object).length === 0) {
            delete obj[key];
          }
        }
      });
    };
    cleanObject(payload);
    const url = ApiConstants.getBranchesOrTags.url
      .replace('{parentAppSid}', parentAppSid)
      .replace('{suiteMode}', String(suiteMode));
    return this.http.post<ApiBranchOrTagResponse>(url, payload, { headers: this.getHeaders(parentAppSid) }).pipe(
      map((response) => ({
        data: response.results.map(item => ({ data: item as any })) as (BranchTreeNode | TagTreeNode)[],
        total: response.total
      }))
    );
  }



  getBranchOrTagFilterOptions(
    refType: RefType,
    header: string,
    value: string | null,
    parentAppSid: string
  ): Observable<FilterOptionsResponse> {
    // For sourceBranchName, we need to request sourceBranchAppSid from API
    let filterField = header;
    if (header === 'sourceBranchName') {
      filterField = 'sourceBranchAppSid';
    }
    
    const payload = {
      pgNo: '0',
      length: '10',
      refType: refType,
      filter: filterField,
      value: value ?? ''
    };
    const url = ApiConstants.getBranchOrTagFilterOptions.url.replace('{parentAppSid}', parentAppSid);
    return this.http.post<Record<string, unknown>[]>(url, payload, {
      headers: this.getHeaders(parentAppSid)
    }).pipe(
      map((response) => {
        // For sourceBranchName filter, extract unique sourceBranchName values from the response
        // The API returns records with sourceBranchAppSid, but we extract sourceBranchName for display and filtering
        if (header === 'sourceBranchName') {
          const values: string[] = [];
          
          // Collect unique sourceBranchName values from the response
          response.forEach((item) => {
            const sourceBranchName = item['sourceBranchName'] as string;
            
            if (sourceBranchName && !values.includes(sourceBranchName)) {
              values.push(sourceBranchName);
            }
          });
          
          // Return branch names as both label and value
          return { data: values };
        }
        
        // For sourceBranchAppSid (legacy), we need to show source branch names but use their IDs as values
        // The response contains branches/tags that have a sourceBranchAppSid field
        // We extract unique sourceBranchAppSid values and try to find their names
        if (header === 'sourceBranchAppSid') {
          const labelMap: Record<string, string> = {};
          const sidToRecordMap: Map<string, Record<string, unknown>> = new Map();
          const values: string[] = [];
          
          // Build a map of all records by their various IDs for quick lookup
          response.forEach((item) => {
            const itemSid = item['sid'] as string;
            const itemDefaultAppSid = item['defaultAppSid'] as string;
            const itemAppSid = item['appSid'] as string;
            
            if (itemSid) sidToRecordMap.set(itemSid, item);
            if (itemDefaultAppSid) sidToRecordMap.set(itemDefaultAppSid, item);
            if (itemAppSid) sidToRecordMap.set(itemAppSid, item);
          });
          
          // Collect unique sourceBranchAppSid values
          response.forEach((item) => {
            const sourceSid = item['sourceBranchAppSid'] as string;
            const sourceBranchName = item['sourceBranchName'] as string;
            
            if (sourceSid && !values.includes(sourceSid)) {
              values.push(sourceSid);
              
              // Try to get the source branch name from various sources
              if (sourceBranchName) {
                // API provides sourceBranchName directly
                labelMap[sourceSid] = sourceBranchName;
              } else {
                // Try to find the source branch record to get its name
                const sourceRecord = sidToRecordMap.get(sourceSid);
                if (sourceRecord?.['refsName']) {
                  labelMap[sourceSid] = sourceRecord['refsName'] as string;
                }
              }
            }
          });
          
          return { data: values, labelMap };
        }
        
        // Extract the field values from the response array based on header
        const values = response
          .map((item) => item[header] as string)
          .filter((val): val is string => val !== null && val !== undefined && val !== '');
        // Remove duplicates
        const uniqueValues = [...new Set(values)];
        return { data: uniqueValues };
      })
    );
  }

  getLastUpdatedDetails(branchId: string): Observable<LastUpdatedDetails> {
    const url = ApiConstants.getLastUpdatedDetails.url.replace('{branchId}', branchId);
    return this.http.get<LastUpdatedDetails>(url, { headers: this.getHeaders() });
  }

  updateComments(appId: string, comment: string): Observable<unknown> {
    const payload = {
      sid: appId,
      comments: comment
    };
    return this.http.put(ApiConstants.updateComments.url, payload, { headers: this.getHeaders() });
  }

  mapToFilterOptions(data: string[]): FilterOption[] {
    return data.map((val) => ({ label: val, value: val }));
  }

  /**
   * Generates a consistent background color based on a string (e.g., username)
   * @param str - The string to hash for color generation
   * @param saturation - Color saturation percentage (default: 50)
   * @param lightness - Color lightness percentage (default: 50)
   * @returns HSL color string
   */
  getBgColor(str: string, saturation = 50, lightness = 50): string {
    let hash = 0;
    if (str) {
      for (let i = 0; i < str.length; i++) {
        hash = (str.codePointAt(i) ?? 0) + ((hash << 5) - hash);
      }
    }
    return `hsl(${hash % 360}, ${saturation}%, ${lightness}%)`;
  }

  /**
   * Get merge changes between two branches
   * @param request - The source and target branch IDs
   * @returns Observable with the merge changes response
   */
  getBranchMergeChanges(request: MergeChangesRequest): Observable<any> {
    const payload = {
      sourceBranchId: request.sourceBranchId,
      targetBranchId: request.targetBranchId,
      ignoreOnGoingMergeValidation: request.ignoreOnGoingMergeValidation ?? false
    };
    return this.httpBaseService.post(
      { ...ApiConstants.getBranchMergeChanges, showSpinner: false, showErrorMsg: false },
      payload
    );
  }

  /**
   * Get last successful merge history tracker for source and target branches.
   */
  getLastSuccessfulMerge(request: MergeChangesRequest): Observable<any> {
    const payload = {
      sourceBranchId: request.sourceBranchId,
      targetBranchId: request.targetBranchId
    };
    return this.httpBaseService.post(
      { ...ApiConstants.getLastSuccessfulMerge, showSpinner: false, showErrorMsg: false },
      payload
    );
  }

  /**
   * Validate if there's an ongoing merge for the given branches (without showing spinner)
   * @param request - The source and target branch IDs
   * @returns Observable with the merge changes response or error if ongoing merge exists
   */
  validateOngoingMerge(request: MergeChangesRequest): Observable<any> {
    const payload = {
      sourceBranchId: request.sourceBranchId,
      targetBranchId: request.targetBranchId,
      ignoreOnGoingMergeValidation: false
    };
    return this.httpBaseService.post(
      { ...ApiConstants.getBranchMergeChanges, showSpinner: false, showErrorMsg: false },
      payload
    );
  }

  /**
   * Check if a branch is eligible for merge (no active merge in progress)
   * @param branchId - The branch ID to check
   * @returns Observable with eligibility response { eligible: boolean, reason?: string }
   */
  checkMergeEligibility(branchId: string): Observable<any> {
    return this.httpBaseService.get(
      { ...ApiConstants.checkMergeEligibility, showSpinner: false, showErrorMsg: false },
      { branchId }
    );
  }

  /**
   * Get auto-selected components for merge (Step 3)
   * @param request - Selected components and dependent components map
   * @returns Observable with the merge changes response including auto-selected components
   */
  getMergeAutoSelect(request: AutoSelectRequest | AutoSelectRequest[]): Observable<any> {
    return this.httpBaseService.post(
      { ...ApiConstants.getMergeAutoSelect, showSpinner: false, showErrorMsg: false },
      request
    );
  }

  /**
   * Cancel an ongoing merge operation
   * @param mergeId - The merge history tracker ID
   * @returns Observable with the response
   */
  cancelMergeHistoryTracker(mergeId: string): Observable<any> {
    return this.httpBaseService.put(
      { ...ApiConstants.cancelMergeHistoryTracker, showSpinner: true, showErrorMsg: false },
      { mergeId }
    );
  }

  /**
   * Merge branches with selected components
   * @param request - The merge request with selected/unselected components
   * @returns Observable with the merge response
   */
  mergeBranch(request: MergeBranchRequest): Observable<any> {
    return this.httpBaseService.post(
      { ...ApiConstants.mergeBranch, showSpinner: false, showErrorMsg: false },
      request
    );
  }

  deleteBranch(branchId: string, toDeleteInGit: boolean): Observable<unknown> {
    const url = `${ApiConstants.deleteBranch.url.replace('{branchId}', branchId)}?toDeleteInGit=${toDeleteInGit}`;
    return this.http.delete(url, { headers: this.getHeaders() });
  }

  deleteTag(tagId: string, toDeleteInGit: boolean): Observable<unknown> {
    const url = `${ApiConstants.deleteTag.url.replace('{tagId}', tagId)}?toDeleteInGit=${toDeleteInGit}`;
    return this.http.delete(url, { headers: this.getHeaders() });
  }

  checkForUnpublishedChanges(appId: string): Observable<unknown> {
    // referredAppId ensures x-rappit-application header matches the path variable
    return this.httpBaseService.get(ApiConstants.checkUnpublishedChanges, { appId, referredAppId: appId });
  }

  /**
   * Navigates to default branch for the given parent app.
   * Falls back to app management page if default branch is not available.
   */
  navigateToDefaultBranch(parentAppSid?: string): Promise<boolean> {
    const orgId = this.appGlobalService.get('orgId') as string;
    const lookupParentAppSid = parentAppSid || (this.appGlobalService.get('parentAppSid') as string);

    if (!lookupParentAppSid) {
      return this.router
        .navigate(['/home', orgId, 'appId', 'settings', 'appmanagement'])
        .catch((error) => {
          console.error(error);
          return false;
        });
    }

    return firstValueFrom(
      this.getBranchesOrTags({
        orgId: orgId,
        refType: 'BRANCH',
        filters: {},
        sort: '',
        dir: '',
        page: 1,
        length: 100,
        parentAppSid: lookupParentAppSid
      })
    )
      .then((branchResponse) => {
        const branches = branchResponse.data.map((item) => item.data);
        const defaultBranch = branches.find((branch) => branch.isDefaultBranch === true);

        if (defaultBranch?.sid) {

          return this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => this.router.navigate(['/home', orgId, defaultBranch.sid, 'gathering', 'documents']));
        }

        return this.router.navigate(['/home', orgId, 'appId', 'settings', 'appmanagement']);
      })
      .catch((error) => {
        console.error(error);
        return this.router.navigate(['/home', orgId, 'appId', 'settings', 'appmanagement']);         
      });
  }

    getAppWithOutMenu(appId: string): any {
    const apiConstants = { ...ApiConstants.getAppWithOutMenu };
    apiConstants.url = `${apiConstants.url}${appId}`;
    return this.httpBaseService[apiConstants.method](apiConstants);
  }

}
